// Thin client for the Math Ladder API. Same origin in production (/api), proxied by Vite in development.

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: { 'x-requested-with': 'ml', ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError('Could not reach Math Ladder. Check your connection and try again.', 0, 'network');
  }
  const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
  if (!res.ok) throw new ApiError(data?.error || `Request failed (${res.status}).`, res.status, data?.code);
  return data as T;
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b: unknown = {}) => request<T>('POST', p, b),
  patch: <T>(p: string, b: unknown) => request<T>('PATCH', p, b),
  put: <T>(p: string, b: unknown) => request<T>('PUT', p, b),
  del: <T>(p: string, b?: unknown) => request<T>('DELETE', p, b),
};

// ---------------------------------------------------------------- shapes returned by the API
export interface Account { id: string; email: string; name: string | null; emailVerified: boolean; hasPassword: boolean; google: boolean; createdAt: string }


export interface Student {
  id: string; name: string; receiptName: string | null; grade: string | null;
  currentStage: number | null; archived: boolean; createdAt: string;
}

export interface Doc {
  id: string; kind: 'sheet' | 'set' | 'book' | 'test' | 'sample'; stage: number; step: number | null; sheet: number | null; test: string | null;
  studentId: string | null; status: 'queued' | 'running' | 'done' | 'failed'; pages: number | null; title: string; error: string | null;
  createdAt: string; finishedAt: string | null; downloadable?: boolean;
}

export interface Product { code: string; kind: 'stage' | 'bundle' | 'pass'; name: string; description: string; stages: number[]; passDays: number | null; priceCents: number }

export interface OrderItem { id: string; productCode: string; studentId: string | null; studentLabel: string; description: string; unitCents: number; discountCents: number; taxCents: number; amountCents: number; refundedCents: number }
export interface Order {
  id: string; receiptNo: number; status: string; createdAt: string; paidAt: string | null;
  subtotalCents: number; discountCents: number; taxCents: number; totalCents: number; refundedCents: number; items: OrderItem[];
}

// ---------------------------------------------------------------- browser error reporting (self-hosted)
let reported = 0;
export function reportError(message: string, stack?: string | null) {
  if (reported++ > 5) return;
  fetch('/api/client-errors', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-requested-with': 'ml' },
    body: JSON.stringify({ source: 'admin', message: String(message).slice(0, 2000), stack: stack?.slice(0, 8000) ?? null, url: location.pathname }),
  }).catch(() => {});
}
